import { useState } from "react";
import dayjs from "dayjs";

import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Table from "react-bootstrap/Table";
import { FaEllipsisV, FaEye, FaInfoCircle } from "react-icons/fa";

import { formatIdentifier } from "@/utils/formatIdentifier";
import { handleColumnToggle } from "@/utils/handleColumnTogle";
import { getAge } from "@/utils/dateUtil";
import { DetailedCitizenAcsVisitsIndicatorC5Data } from "@/services/esus/indicator/esf/c5/getDetailedCitizensAcsVisitsIndicatorC5";
import { getCitizenScoreStatusDescriptionIndicatorC5 } from "@/utils/indicatorsEsf";
import { Column } from "@/@types/generics/Column";

import { TableSkeletons } from "@/components/TableSkeletons";
import { CardOverlayTrigger } from "@/components/CardOverlayTrigger";

type IndicatorEsfC5CitizensAcsVisitsTableColumns = {
	citizenName: Column;
	citizenAge: Column;
	document: Column;
	healthTeam: Column;
	microarea: Column;
	firstAcsVisitDate: Column;
	lastAcsVisitDate: Column;
};

type IndicatorEsfC5CitizensAcsVisitsTableProps = {
	citizensAcsVisits: DetailedCitizenAcsVisitsIndicatorC5Data[];
	isLoading: boolean;
	openCitizenHistory: (citizenWithHomeVisits: DetailedCitizenAcsVisitsIndicatorC5Data) => void;
};

export function IndicatorEsfC5CitizensAcsVisitsTable({
	citizensAcsVisits,
	isLoading,
	openCitizenHistory,
}: Readonly<IndicatorEsfC5CitizensAcsVisitsTableProps>) {
	const [columns, setColumns] = useState<IndicatorEsfC5CitizensAcsVisitsTableColumns>({
		citizenName: { title: "Nome do cidadão", visible: true },
		citizenAge: { title: "Idade", visible: true },
		document: { title: "Documento", visible: true },
		healthTeam: { title: "Equipe", visible: false },
		microarea: { title: "MA", visible: false },
		firstAcsVisitDate: { title: "1ª Visita", visible: true },
		lastAcsVisitDate: { title: "Última Visita", visible: true },
	});

	return (
		<Table striped bordered hover responsive>
			<thead className="text-center fs-7">
				<tr className="fw-bolder text-center">
					<th className={`text-start px-2 ${!columns.citizenName.visible && "d-none"}`}>
						{columns.citizenName.title}
					</th>
					<th className={`px-2 ${!columns.citizenAge.visible && "d-none"}`}>
						{columns.citizenAge.title}
					</th>
					<th className={`px-2 ${!columns.document.visible && "d-none"}`}>
						{columns.document.title}
					</th>
					<th className={`px-2 ${!columns.healthTeam.visible && "d-none"}`}>
						{columns.healthTeam.title}
					</th>
					<th className={`px-2 ${!columns.microarea.visible && "d-none"}`}>
						{columns.microarea.title}
					</th>
					<th className={`px-2 ${!columns.firstAcsVisitDate.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.firstAcsVisitDate.title}
							<CardOverlayTrigger
								header="1ª Visita"
								message="1ª Visita realizada por ACS ou TACS nos últimos 12 meses"
								icon={<FaInfoCircle className="text-muted" />}
							/>
						</div>
					</th>
					<th className={`px-2 ${!columns.lastAcsVisitDate.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.lastAcsVisitDate.title}
							<CardOverlayTrigger
								header="Última Visita"
								message="Última Visita realizada por ACS ou TACS nos últimos 12 meses (valida somente com intervalo mínimo de 30 dias da 1ª visita)"
								icon={<FaInfoCircle className="text-muted" />}
							/>
						</div>
					</th>
					<th className="text-nowrap pb-0">
						Ações
						<OverlayTrigger
							trigger="click"
							placement="bottom"
							overlay={
								<Popover>
									<Popover.Body>
										<Form className="d-flex flex-column gap-2">
											{Object.keys(columns).map((key, index) => (
												<Form.Check
													key={`citizen-attendances-table-column-${
														index + 1
													}`}
													type="checkbox"
													label={
														columns[
															key as keyof IndicatorEsfC5CitizensAcsVisitsTableColumns
														].title
													}
													checked={
														columns[
															key as keyof IndicatorEsfC5CitizensAcsVisitsTableColumns
														].visible
													}
													onChange={() =>
														handleColumnToggle(
															key as keyof IndicatorEsfC5CitizensAcsVisitsTableColumns,
															setColumns
														)
													}
												/>
											))}
										</Form>
									</Popover.Body>
								</Popover>
							}
							rootClose
						>
							<Button
								title="Exibição de colunas da tabela"
								variant="white"
								className="px-2"
								disabled={isLoading}
							>
								<FaEllipsisV />
							</Button>
						</OverlayTrigger>
					</th>
				</tr>
			</thead>
			<tbody className="fs-8">
				{!isLoading && citizensAcsVisits.length > 0
					? citizensAcsVisits.map((citizenWithAcsVisits, index) => (
							<tr key={`citizen-attendances-table-row-${index + 1}`}>
								<td className={`px-2 ${!columns.citizenName.visible && "d-none"}`}>
									<div className="d-flex flex-column">
										<div>{citizenWithAcsVisits.fullName}</div>
										<div className="text-muted">
											{citizenWithAcsVisits.birthdate
												? dayjs(citizenWithAcsVisits.birthdate).format(
														"DD/MM/YYYY"
												  )
												: ""}
										</div>
									</div>
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.citizenAge.visible && "d-none"
									}`}
								>
									{getAge(citizenWithAcsVisits.birthdate)}
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.document.visible && "d-none"
									}`}
								>
									{citizenWithAcsVisits.cpf
										? formatIdentifier(citizenWithAcsVisits.cpf)
										: citizenWithAcsVisits.cns ?? ""}
								</td>
								<td className={`px-2 ${!columns.healthTeam.visible && "d-none"}`}>
									<div className="d-flex flex-column">
										<div>{citizenWithAcsVisits.healthTeam?.name ?? ""}</div>
										<div className="text-muted">
											{citizenWithAcsVisits.healthTeam?.ine ?? ""}
										</div>
									</div>
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.microarea.visible && "d-none"
									}`}
								>
									{citizenWithAcsVisits.area ?? "FA"}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.firstAcsVisitDate.visible && "d-none"
									}`}
								>
									<div className="d-flex flex-column">
										<div>
											{getCitizenScoreStatusDescriptionIndicatorC5(
												citizenWithAcsVisits.firstAcsHomeVisitStatus
											)}
										</div>
										<div className="text-muted">
											{citizenWithAcsVisits.firstAcsHomeVisit?.date
												? dayjs(
														citizenWithAcsVisits.firstAcsHomeVisit.date
												  ).format("DD/MM/YYYY")
												: ""}
										</div>
									</div>
								</td>
								<td
									className={`text-center px-2 ${
										!columns.lastAcsVisitDate.visible && "d-none"
									}`}
								>
									<div className="d-flex flex-column">
										<div>
											{getCitizenScoreStatusDescriptionIndicatorC5(
												citizenWithAcsVisits.twoHomeVisitsStatus
											)}
										</div>
										<div className="text-muted">
											{citizenWithAcsVisits.lastAcsHomeVisit?.date
												? dayjs(
														citizenWithAcsVisits.lastAcsHomeVisit.date
												  ).format("DD/MM/YYYY")
												: ""}
										</div>
									</div>
								</td>
								<td className="text-center">
									<Button
										className="bg-primary py-1 px-3"
										onClick={() => openCitizenHistory(citizenWithAcsVisits)}
										title="Histórico de visitas do ACS"
									>
										<FaEye className="fs-4" />
									</Button>
								</td>
							</tr>
					  ))
					: isLoading && (
							<TableSkeletons
								numberOfCells={
									Object.values(columns).reduce(
										(count, column) => count + (column?.visible ? 1 : 0),
										0
									) + 1
								}
							/>
					  )}
			</tbody>
		</Table>
	);
}
